import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import {
  useDisconnectSlack,
  useE2EAgentConfiguration,
  usePersonalNotificationSettings,
  usePersonalSlackSettings,
  useSlackSettings,
  useStartSlackOAuth,
  useUpdatePersonalSlack,
  useUpdatePersonalNotifications,
} from "../api/admin";
import { useAuth } from "../auth/AuthContext";
import { canAdministerWorkspace } from "../auth/roles";
import { Button, ConfirmDialog } from "../components/ui";
import {
  ErrorState,
  InlineNotice,
  Loading,
  PageHeader,
  Panel,
} from "../components/WorkspacePrimitives";
import SettingsLayout from "./SettingsLayout";
import "./settings.css";

export default function NotificationsSettingsPage() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const configuration = useE2EAgentConfiguration();
  const notifications = usePersonalNotificationSettings();
  const slackAvailable = configuration.data?.client_slack_available === true;
  const settings = useSlackSettings(
    canAdministerWorkspace(auth) && slackAvailable,
  );
  const personal = usePersonalSlackSettings(slackAvailable);
  const startOAuth = useStartSlackOAuth();
  const disconnect = useDisconnectSlack();
  const updatePersonal = useUpdatePersonalSlack();
  const updateNotifications = useUpdatePersonalNotifications();

  useEffect(() => {
    const result = searchParams.get("slack");
    if (result === "connected") toast.success("Slack workspace connected.");
    if (result === "error")
      toast.error("Slack authorization was not completed.");
    if (result !== null) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const busy =
    startOAuth.isPending ||
    disconnect.isPending ||
    updatePersonal.isPending ||
    updateNotifications.isPending;
  const connect = () =>
    startOAuth.mutate(undefined, {
      onSuccess: ({ authorization_url }) =>
        window.location.assign(authorization_url),
      onError: (error) => toast.error(error.message),
    });

  return (
    <SettingsLayout>
      <div className="page page--workspace slack-settings-page">
        <PageHeader className="settings-page-header">
          <h1>Notifications</h1>
        </PageHeader>

        {notifications.isPending && !notifications.data ? (
          <Loading label="Loading email settings…" />
        ) : notifications.error && !notifications.data ? (
          <ErrorState
            error={notifications.error}
            onRetry={() => notifications.refetch()}
          />
        ) : notifications.data ? (
          <Panel
            title="Email notifications"
            meta={notifications.data.email ?? undefined}
          >
            <div className="slack-settings-row">
              <div>
                <strong>Run completion emails</strong>
                <small>
                  Get an email whenever a model run you started succeeds, fails,
                  or is canceled after execution begins.
                </small>
                {!notifications.data.email_available && (
                  <small>
                    Add an email address to your workspace profile before
                    enabling completion emails.
                  </small>
                )}
              </div>
              <Button
                tone={
                  notifications.data.email_enabled ? "secondary" : "primary"
                }
                disabled={!notifications.data.email_available || busy}
                onClick={() =>
                  updateNotifications.mutate(
                    !notifications.data.email_enabled,
                    {
                      onSuccess: (updated) =>
                        toast.success(
                          updated.email_enabled
                            ? "Email notifications enabled."
                            : "Email notifications disabled.",
                        ),
                      onError: (error) => toast.error(error.message),
                    },
                  )
                }
              >
                {notifications.data.email_enabled
                  ? "Disable emails"
                  : "Enable emails"}
              </Button>
            </div>
          </Panel>
        ) : null}

        {slackAvailable &&
          canAdministerWorkspace(auth) &&
          (settings.isPending && !settings.data ? (
            <Loading label="Loading Slack settings…" />
          ) : settings.error && !settings.data ? (
            <ErrorState
              error={settings.error}
              onRetry={() => settings.refetch()}
            />
          ) : settings.data ? (
            <Panel
              title="Slack workspace"
              meta={settings.data.workspace_name ?? undefined}
            >
              <div className="slack-settings-row">
                <div>
                  <strong>
                    {settings.data.connected
                      ? settings.data.workspace_name
                      : "No workspace connected"}
                  </strong>
                  <small>
                    {settings.data.connected
                      ? `Workspace ${settings.data.workspace_id}`
                      : "Connect the Slack app to enable personal DMs."}
                  </small>
                </div>
                <div className="slack-workspace-actions">
                  <Button
                    tone={settings.data.connected ? "secondary" : "primary"}
                    disabled={busy}
                    onClick={connect}
                  >
                    {settings.data.connected
                      ? "Reconnect Slack"
                      : "Connect Slack"}
                  </Button>
                  {settings.data.connected && (
                    <Button
                      tone="danger"
                      disabled={busy}
                      onClick={() => setConfirmingDisconnect(true)}
                    >
                      Disconnect
                    </Button>
                  )}
                </div>
              </div>
            </Panel>
          ) : null)}

        {configuration.isPending && !configuration.data ? (
          <Loading label="Loading Slack availability…" />
        ) : configuration.error && !configuration.data ? (
          <ErrorState
            error={configuration.error}
            onRetry={() => configuration.refetch()}
          />
        ) : !slackAvailable ? (
          <Panel title="Slack notifications unavailable">
            <InlineNotice tone="warning">
              This deployment has not been configured with Slack OAuth
              credentials. An administrator must finish the Slack app setup
              before personal DMs can be enabled.
            </InlineNotice>
          </Panel>
        ) : personal.isPending && !personal.data ? (
          <Loading label="Loading personal Slack settings…" />
        ) : personal.error && !personal.data ? (
          <ErrorState
            error={personal.error}
            onRetry={() => personal.refetch()}
          />
        ) : personal.data ? (
          <Panel
            title="Slack direct messages"
            meta={personal.data.email ?? undefined}
          >
            <div className="slack-settings-row">
              <div>
                <strong>Direct messages from the Slack bot</strong>
                <small>
                  Get a DM whenever a model run you started finishes.
                </small>
                {!personal.data.available && (
                  <small>
                    An administrator must connect Slack, and your workspace
                    profile must have an email matching your Slack account.
                  </small>
                )}
              </div>
              <Button
                tone={personal.data.enabled ? "secondary" : "primary"}
                disabled={!personal.data.available || busy}
                onClick={() =>
                  updatePersonal.mutate(!personal.data.enabled, {
                    onSuccess: (updated) =>
                      toast.success(
                        updated.enabled
                          ? "Personal Slack notifications enabled."
                          : "Personal Slack notifications disabled.",
                      ),
                    onError: (error) => toast.error(error.message),
                  })
                }
              >
                {personal.data.enabled ? "Disable my DMs" : "Enable my DMs"}
              </Button>
            </div>
          </Panel>
        ) : null}
      </div>
      {confirmingDisconnect && (
        <ConfirmDialog
          title="Disconnect Slack?"
          description="Model-run notifications will stop until an administrator reconnects a workspace."
          confirmLabel="Disconnect"
          busy={disconnect.isPending}
          onCancel={() => setConfirmingDisconnect(false)}
          onConfirm={() =>
            disconnect.mutate(undefined, {
              onSuccess: () => {
                toast.success("Slack disconnected.");
                setConfirmingDisconnect(false);
              },
              onError: (error) => toast.error(error.message),
            })
          }
        />
      )}
    </SettingsLayout>
  );
}
